import { db } from './database';
import { WheelEntry } from '../types';
import { generateUniqueId, secureShuffle } from '../utils/random';

export async function getEntriesForWheel(wheelId: string): Promise<WheelEntry[]> {
  return await db.entries.where('wheelId').equals(wheelId).sortBy('position');
}

export async function addEntry(
  wheelId: string,
  label: string,
  color?: string,
  weight: number = 1
): Promise<WheelEntry> {
  const currentEntries = await getEntriesForWheel(wheelId);
  const position = currentEntries.length;
  const entry: WheelEntry = {
    id: generateUniqueId(),
    wheelId,
    label: label.trim(),
    color,
    position,
    weight: weight > 0 ? weight : 1,
    createdAt: Date.now(),
  };

  await db.entries.add(entry);
  await db.wheels.update(wheelId, { updatedAt: Date.now() });
  return entry;
}

export async function bulkAddEntries(wheelId: string, labels: string[]): Promise<WheelEntry[]> {
  const cleanLabels = labels.map((l) => l.trim()).filter((l) => l.length > 0);
  if (cleanLabels.length === 0) return [];

  const currentEntries = await getEntriesForWheel(wheelId);
  let startPos = currentEntries.length;
  const now = Date.now();

  const newEntries: WheelEntry[] = cleanLabels.map((label, idx) => ({
    id: generateUniqueId(),
    wheelId,
    label,
    position: startPos + idx,
    weight: 1,
    createdAt: now + idx,
  }));

  await db.entries.bulkAdd(newEntries);
  await db.wheels.update(wheelId, { updatedAt: Date.now() });
  return newEntries;
}

export async function updateEntry(
  entryId: string,
  updates: Partial<Omit<WheelEntry, 'id' | 'wheelId' | 'createdAt'>>
): Promise<void> {
  const entry = await db.entries.get(entryId);
  if (!entry) return;

  await db.entries.update(entryId, updates);
  await db.wheels.update(entry.wheelId, { updatedAt: Date.now() });
}

export async function deleteEntry(entryId: string): Promise<void> {
  const entry = await db.entries.get(entryId);
  if (!entry) return;

  const wheelId = entry.wheelId;
  await db.entries.delete(entryId);

  // Normalize remaining positions
  const remaining = await getEntriesForWheel(wheelId);
  await db.transaction('rw', db.entries, db.wheels, async () => {
    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i].position !== i) {
        await db.entries.update(remaining[i].id, { position: i });
      }
    }
    await db.wheels.update(wheelId, { updatedAt: Date.now() });
  });
}

export async function clearAllEntries(wheelId: string): Promise<void> {
  await db.entries.where('wheelId').equals(wheelId).delete();
  await db.wheels.update(wheelId, { updatedAt: Date.now() });
}

export async function shuffleEntriesInWheel(wheelId: string): Promise<void> {
  const entries = await getEntriesForWheel(wheelId);
  if (entries.length <= 1) return;

  const shuffled = secureShuffle(entries);
  await db.transaction('rw', db.entries, db.wheels, async () => {
    for (let i = 0; i < shuffled.length; i++) {
      await db.entries.update(shuffled[i].id, { position: i });
    }
    await db.wheels.update(wheelId, { updatedAt: Date.now() });
  });
}

export async function sortEntriesAZInWheel(wheelId: string): Promise<void> {
  const entries = await getEntriesForWheel(wheelId);
  if (entries.length <= 1) return;

  const sorted = [...entries].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  await db.transaction('rw', db.entries, db.wheels, async () => {
    for (let i = 0; i < sorted.length; i++) {
      await db.entries.update(sorted[i].id, { position: i });
    }
    await db.wheels.update(wheelId, { updatedAt: Date.now() });
  });
}

export async function deduplicateEntriesInWheel(wheelId: string): Promise<number> {
  const entries = await getEntriesForWheel(wheelId);
  const seen = new Set<string>();
  const toDelete: string[] = [];

  for (const entry of entries) {
    const key = entry.label.trim().toLowerCase();
    if (seen.has(key)) {
      toDelete.push(entry.id);
    } else {
      seen.add(key);
    }
  }

  if (toDelete.length > 0) {
    await db.transaction('rw', db.entries, db.wheels, async () => {
      await db.entries.bulkDelete(toDelete);
      const remaining = await getEntriesForWheel(wheelId);
      for (let i = 0; i < remaining.length; i++) {
        await db.entries.update(remaining[i].id, { position: i });
      }
      await db.wheels.update(wheelId, { updatedAt: Date.now() });
    });
  }

  return toDelete.length;
}
